import { useCallback, useState } from 'react';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import Logger from '@framework/sentry/Logger';
import { useMessageManager } from '@framework/message';
import { formatTimeLeft } from '@/creditflowresearch/ui/common/utils/dateTime';

type TExportPhase = 'idle' | 'generating' | 'downloading';

interface IExportProgress {
  phase: TExportPhase,
  percent: number,
  statusText: string,
}

const IDLE: IExportProgress = { phase: 'idle', percent: 0, statusText: '' };

const DOWNLOAD_READY_THRESHOLD_SECONDS = 5;
const clampPercent = (value: number): number => Math.max(0, Math.min(100, value));

type TOnExportProgress = (percent: number, timeLeftSeconds: number) => void;
type TExportOperation = (onProgress: TOnExportProgress) => Promise<void>;

interface ICreateUseExportBondScreenerOptions {
  errorLabel: string,
}

const createUseExportProgress = ({ errorLabel }: ICreateUseExportBondScreenerOptions) => () => {
  const { getErrorMessage } = useMessageManager();
  const [progress, setProgress] = useState<IExportProgress>(IDLE);

  const handleProgress = useCallback<TOnExportProgress>((percent, timeLeftSeconds) => {
    const clamped = clampPercent(percent);
    const isAlmostReady = timeLeftSeconds < DOWNLOAD_READY_THRESHOLD_SECONDS;

    setProgress({
      phase: isAlmostReady ? 'downloading' : 'generating',
      percent: clamped,
      statusText: isAlmostReady ? 'Preparing to download' : formatTimeLeft(timeLeftSeconds),
    });
  }, []);

  const resetProgress = useCallback((): void => setProgress(IDLE), []);

  const startExport = useCallback((): void => {
    setProgress({ phase: 'generating', percent: 0, statusText: 'Generating' });
  }, []);

  const run = useCallback(async (operation: TExportOperation): Promise<void> => {
    if (progress.phase !== 'idle') return;

    startExport();

    try {
      await operation(handleProgress);
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
      Logger.error({ message: errorLabel, error });
    } finally {
      resetProgress();
    }
  }, [progress.phase, startExport, handleProgress, resetProgress, getErrorMessage]);

  return {
    isExporting: progress.phase !== 'idle',
    isGenerating: progress.phase === 'generating',
    isDownloading: progress.phase === 'downloading',
    percent: progress.percent,
    statusText: progress.statusText,
    run,
  };
};

export default createUseExportProgress;
export type { ICreateUseExportBondScreenerOptions, TOnExportProgress, TExportOperation };
